
import React from 'react';
import { Globe } from 'lucide-react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { Button } from "@/components/ui/button";
import { Volume2 } from "lucide-react";
import { PrayerVoice } from "@/hooks/usePrayerGenerator";

interface PrayerFeatureCardProps {
  title: string;
  description: string;
  image: string;
  voices?: PrayerVoice[];
  onPlayPreview?: (voiceId: string, previewUrl: string) => void;
  playingAudio?: string | null;
}

export const PrayerFeatureCard: React.FC<PrayerFeatureCardProps> = ({
  title,
  description,
  image,
  voices,
  onPlayPreview,
  playingAudio
}) => {
  const cardRef = React.useRef(null);
  const { scrollYProgress } = useScroll({
    target: cardRef,
    offset: ["start end", "center center"]
  });
  const rotate = useTransform(scrollYProgress, [0, 1], [0, 360]);
  const textVariants = {
    hidden: {
      opacity: 0,
      x: -50
    },
    visible: {
      opacity: 1,
      x: 0,
      transition: {
        duration: 0.6,
        ease: "easeOut"
      }
    }
  };

  return (
    <motion.div 
      ref={cardRef} 
      initial="hidden" 
      whileInView="visible" 
      viewport={{ once: true }} 
      className="bg-white rounded-2xl p-8 shadow-lg relative"
    >
      <div className="flex flex-col md:flex-row items-start gap-8">
        <motion.div className="flex-1" variants={textVariants}>
          <div className="flex items-center gap-3 mb-4">
            <Globe className="text-heaven-gold w-6 h-6" />
            <h3 className="text-2xl font-playfair text-[#1A1F2C]">{title}</h3>
          </div>
          <p className="text-gray-600">{description}</p>
          
          {voices && onPlayPreview && (
            <div className="hidden md:block mt-auto pt-6">
              <p className="text-gray-500 mb-3">Press To Preview Voices</p>
              <div className="flex flex-wrap gap-3">
                {voices.map(voice => (
                  <Button 
                    key={voice.id} 
                    variant="outline" 
                    onClick={() => onPlayPreview(voice.id, voice.previewUrl)} 
                    className={`flex items-center justify-center gap-2.5 px-6 py-2 text-sm whitespace-nowrap
                      ${playingAudio === voice.id 
                        ? 'bg-[#1A6AFF] border-[#D4AF37] text-white animate-pulse' 
                        : 'bg-[#1A6AFF] border-[#D4AF37] text-white hover:bg-[#D4AF37] hover:text-[#1A6AFF] transition-colors'}`}
                  >
                    <Volume2 className={`w-3 h-3 ${playingAudio === voice.id ? 'animate-pulse' : ''}`} />
                    {voice.name}
                  </Button>
                ))}
              </div>
            </div>
          )}
        </motion.div>
        
        <div className="w-full md:w-auto flex flex-col items-center md:items-end">
          <motion.div 
            className="w-32 h-32 flex-shrink-0 flex items-center justify-center mx-auto md:ml-auto" 
            style={{ rotate }}
          >
            <img src={image} alt={title} className="w-full h-full rounded-full object-cover" />
          </motion.div>

          {voices && onPlayPreview && (
            <div className="mt-6 w-full md:hidden">
              <p className="text-gray-500 mb-3 text-center">Press To Preview Voices</p>
              <div className="flex flex-wrap justify-center gap-3">
                {voices.map(voice => (
                  <Button 
                    key={voice.id} 
                    variant="outline" 
                    onClick={() => onPlayPreview(voice.id, voice.previewUrl)} 
                    className={`flex items-center justify-center gap-2.5 px-6 py-2 text-sm whitespace-nowrap
                      ${playingAudio === voice.id 
                        ? 'bg-[#1A6AFF] border-[#D4AF37] text-white animate-pulse' 
                        : 'bg-[#1A6AFF] border-[#D4AF37] text-white hover:bg-[#D4AF37] hover:text-[#1A6AFF] transition-colors'}`}
                  >
                    <Volume2 className={`w-3 h-3 ${playingAudio === voice.id ? 'animate-pulse' : ''}`} />
                    {voice.name}
                  </Button>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </motion.div>
  );
};
